import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
} from 'react';

// Componentele de baza ale Elsei (Tailwind). Mici si putine: paginile sunt tabele si formulare.
// Pluto are aspectul lui (apps/pluto/src/ui, docs/21 §2) si ia de aici doar routerul.
// Aspectul e cel din kitul ELSA by WebCustom (docs/22 §11): culorile `cod`, `verde`, `semnal`,
// `grafit`, `otel`, `hartie`, `cerneala`, `gri-*` si utilitarul `eticheta` vin din
// apps/elsa/src/styles.css. Colturi drepte, linii de 2 px, fara umbre; rosul doar pentru actiuni
// si pentru lucrul care trebuie sa sara in ochi.

export function cx(...c: (string | false | null | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

export function Page({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 px-4 py-6 sm:px-6">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b-2 border-cerneala pb-3">
        <h1 className="text-2xl leading-tight font-extrabold tracking-tight sm:text-[28px]">
          {title}
        </h1>
        {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
      </div>
      {children}
    </div>
  );
}

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('border-2 border-cerneala bg-hartie', className)}>
      {title || actions ? (
        <header className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-b-2 border-cerneala px-4 py-2">
          <h2 className="eticheta text-cerneala">{title}</h2>
          {actions}
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'info';
// Paleta kitului n-are verde de „ok” sau albastru de „info”: bine = Verde Adanc, informativ =
// Grafit, atentie = rosu deschis, rau = rosul pentru text.
const tones: Record<Tone, string> = {
  neutral: 'bg-gri-200 text-gri-800',
  good: 'bg-verde text-hartie',
  warn: 'bg-semnal-100 text-semnal-800',
  bad: 'bg-semnal-700 text-hartie',
  info: 'bg-grafit text-hartie',
};

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return (
    <span
      className={cx(
        'inline-flex items-center px-1.5 py-0.5 text-[11px] leading-4 font-bold tracking-wide whitespace-nowrap',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

const STATUS_TONE: Record<string, Tone> = {
  done: 'good',
  complete: 'good',
  running: 'info',
  queued: 'neutral',
  split: 'neutral',
  partial: 'warn',
  truncated: 'warn',
  warn: 'warn',
  cancelled: 'warn',
  rejected: 'bad',
  failed: 'bad',
  error: 'bad',
  fatal: 'bad',
};

const STATUS_LABEL: Record<string, string> = {
  done: 'gata',
  complete: 'completă',
  running: 'în lucru',
  queued: 'în coadă',
  partial: 'parțială',
  truncated: 'trunchiată',
  cancelled: 'anulat',
  rejected: 'refuzată',
  failed: 'eșuată',
  error: 'eroare',
};

export function Status({ value }: { value: string | null | undefined }) {
  const v = value ?? '—';
  return <Badge tone={STATUS_TONE[v] ?? 'neutral'}>{STATUS_LABEL[v] ?? v}</Badge>;
}

export function Button({
  tone = 'default',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'default' | 'primary' | 'danger' | 'ghost';
}) {
  const t = {
    default: 'border-cerneala text-cerneala hover:bg-cerneala/5 active:bg-cerneala/10',
    primary:
      'border-semnal bg-semnal text-cod hover:border-semnal-600 hover:bg-semnal-600 active:border-semnal-700 active:bg-semnal-700',
    danger: 'border-semnal-700 text-semnal-700 hover:bg-semnal-50 active:bg-semnal-100',
    ghost: 'border-transparent hover:bg-cerneala/5 active:bg-cerneala/10',
  }[tone];
  return (
    <button
      type="button"
      className={cx(
        'inline-flex min-h-9 items-center justify-center gap-2 border-2 px-3.5 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-45',
        t,
        className,
      )}
      {...props}
    />
  );
}

const field =
  'min-h-9 w-full border border-gri-500 bg-gri-200/70 px-2.5 py-1.5 text-sm text-cerneala caret-semnal outline-none placeholder:text-gri-600 hover:border-grafit focus:border-semnal focus:ring-1 focus:ring-semnal disabled:opacity-60';

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cx(field, props.className)} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cx(field, props.className)} />;
}

export function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: ReactNode;
  children: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: controlul e copilul elementului
    <label className="block space-y-1">
      <span className="eticheta block text-gri-700">{label}</span>
      {children}
      {help ? <span className="block text-xs text-gri-700">{help}</span> : null}
    </label>
  );
}

export function Table({
  head,
  children,
  empty,
}: {
  head: string[];
  children: ReactNode;
  empty?: boolean;
}) {
  return (
    <div className="-mx-4 overflow-x-auto">
      <table className="w-full min-w-max text-left text-sm tabular-nums">
        <thead className="text-gri-700">
          <tr>
            {head.map((h) => (
              <th key={h || 'actiuni'} className="eticheta border-b-2 border-cerneala px-4 py-2.5">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gri-300 [&>tr:hover]:bg-cerneala/[0.04]">
          {children}
        </tbody>
      </table>
      {empty ? (
        <p className="px-4 py-6 text-center text-sm text-gri-700">Nimic de arătat.</p>
      ) : null}
    </div>
  );
}

export function Td({ children, className }: { children?: ReactNode; className?: string }) {
  return <td className={cx('px-4 py-2.5 align-top', className)}>{children}</td>;
}

export function ErrorBox({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div
      role="alert"
      className="border-2 border-semnal-700 bg-semnal-50 px-3 py-2 text-sm font-medium text-semnal-800"
    >
      {(error as Error).message ?? String(error)}
    </div>
  );
}

export function Note({ children }: { children: ReactNode }) {
  return (
    <div className="border-l-4 border-cerneala bg-gri-200 px-3 py-2 text-sm text-cerneala">
      {children}
    </div>
  );
}

/** O cifra-cheie, ca in macheta. `accent`: cifra care conteaza (rosie); una singura pe ecran. */
export function Stat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  accent?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 border-2 border-cerneala bg-hartie px-4 py-3">
      <div className="eticheta text-gri-700">{label}</div>
      <div
        className={cx(
          'text-2xl leading-none font-extrabold tracking-tight break-words tabular-nums sm:text-[28px]',
          accent ? 'text-semnal' : 'text-cerneala',
        )}
      >
        {value}
      </div>
      {sub ? <div className="text-xs text-gri-700">{sub}</div> : null}
    </div>
  );
}

export function Loading() {
  return <p className="text-sm text-gri-700">Se încarcă…</p>;
}

/** Bara de progres 0..1. */
export function Progress({ value }: { value: number | null | undefined }) {
  const p = Math.round(Math.min(1, Math.max(0, Number(value ?? 0))) * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 overflow-hidden bg-gri-300">
        <div className="h-full bg-cerneala" style={{ width: `${p}%` }} />
      </div>
      <span className="text-xs tabular-nums text-gri-700">{p}%</span>
    </div>
  );
}

export function Json({ value }: { value: unknown }) {
  return (
    <pre className="max-h-80 overflow-auto bg-gri-200 p-2 text-xs">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}
